import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TenantService } from './tenant.service';

/**
 * v1.6.661 — Feature Suggestions service
 *
 * Wraps POST /v1/tenant/feature-suggestions — the in-app channel for
 * operators to submit feature ideas, bug reports, and improvement
 * requests to the platform team.
 *
 * The submitter identity + tenant context are snapshotted server-side
 * from the authenticated tenant-api session — per the spec, the client
 * MUST NOT send submitter_* or tenant_* fields (they are ignored if
 * supplied). So this service's payload type is intentionally minimal:
 * just title, description, type, priority.
 *
 * Validation per spec:
 *   - title:       required | string | min:5  | max:200
 *   - description: required | string | min:10 | max:5000
 *   - type:        required | in:feature_request,bug_report,improvement,other
 *   - priority:    required | in:low,medium,high
 *
 * Permission required: feature_suggestion.create (tenant-api guard).
 */

export type FeatureSuggestionType = 'feature_request' | 'bug_report' | 'improvement' | 'other';
export type FeatureSuggestionPriority = 'low' | 'medium' | 'high';

export interface FeatureSuggestionPayload {
  title: string;
  description: string;
  type: FeatureSuggestionType;
  priority: FeatureSuggestionPriority;
}

@Injectable({ providedIn: 'root' })
export class FeatureSuggestionsService {
  private get base(): string {
    return `${this.tenant.apiBaseUrl}/v1/tenant/feature-suggestions`;
  }

  constructor(private http: HttpClient, private tenant: TenantService) {}

  /** POST /v1/tenant/feature-suggestions — submit a new suggestion. */
  submit(payload: FeatureSuggestionPayload): Observable<any> {
    return this.http.post(this.base, payload);
  }
}
